import { z } from 'zod';

// CNPJ numérico e alfanumérico (IN RFB 2.229/2024 — emitido desde jul/2026).
// Formato: 12 posições [0-9A-Z] + 2 dígitos verificadores numéricos.
// DV: valor = código ASCII − 48 (0–9 → 0–9; A–Z → 17–42), módulo 11, pesos 2..9 da direita p/ esquerda.

const PESOS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** Remove máscara e põe em maiúsculas: "12.abc.345/01de-35" → "12ABC34501DE35". */
export function limparCnpj(v: string): string {
  return v.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

function dv(base: string, pesos: number[]): number {
  let soma = 0;
  for (let i = 0; i < pesos.length; i++) soma += (base.charCodeAt(i) - 48) * pesos[i]!;
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/** Valida formato + dígitos verificadores. Aceita com ou sem máscara. */
export function validarCnpj(v: string): boolean {
  const c = limparCnpj(v);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(c)) return false;
  if (/^(\d)\1{13}$/.test(c)) return false; // 00000000000000, 11111111111111…
  const d1 = dv(c.slice(0, 12), PESOS_DV1);
  const d2 = dv(c.slice(0, 12) + d1, PESOS_DV2);
  return c.endsWith(`${d1}${d2}`);
}

/** "33000167000101" → "33.000.167/0001-01" (funciona para alfanumérico). */
export function formatarCnpj(v: string): string {
  const c = limparCnpj(v);
  if (c.length !== 14) return v;
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}

// ---------------------------------------------------------------- CPF e documento da conta
// A conta (tenant) pode ser de empresa (CNPJ) ou de pessoa física (CPF): vendedor que assina para uso próprio.

/** Valida CPF (11 dígitos + DVs). Aceita com ou sem máscara. */
export function validarCpf(v: string): boolean {
  const c = v.replace(/\D/g, '');
  if (!/^\d{11}$/.test(c) || /^(\d)\1{10}$/.test(c)) return false;
  const dvCpf = (n: number) => {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(c[i]) * (n + 1 - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dvCpf(9) === Number(c[9]) && dvCpf(10) === Number(c[10]);
}

/** "12345678909" → "123.456.789-09" */
export function formatarCpf(v: string): string {
  const c = v.replace(/\D/g, '');
  return c.length === 11 ? `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9)}` : v;
}

export type TipoDocumento = 'CPF' | 'CNPJ';
/** 11 dígitos = CPF; 14 posições = CNPJ (numérico ou alfanumérico). */
export const tipoDocumento = (v: string | null | undefined): TipoDocumento | null =>
  !v ? null : limparCnpj(v).length === 11 ? 'CPF' : 'CNPJ';
export const validarDocumento = (v: string) => (limparCnpj(v).length === 11 ? validarCpf(v) : validarCnpj(v));
export const formatarDocumento = (v: string | null | undefined) => (!v ? '' : tipoDocumento(v) === 'CPF' ? formatarCpf(v) : formatarCnpj(v));

/** Situação cadastral normalizada (Receita Federal). */
export const SITUACOES_CNPJ = ['ATIVA', 'SUSPENSA', 'INAPTA', 'BAIXADA', 'NULA', 'DESCONHECIDA'] as const;
export type SituacaoCnpj = (typeof SITUACOES_CNPJ)[number];

export function normalizarSituacao(v: unknown): SituacaoCnpj {
  const s = String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toUpperCase();
  return (SITUACOES_CNPJ as readonly string[]).includes(s) ? (s as SituacaoCnpj) : 'DESCONHECIDA';
}

/** Contrato único devolvido pelo backend, independente do provedor. */
export const dadosCnpjSchema = z.object({
  cnpj: z.string(),
  razaoSocial: z.string(),
  nomeFantasia: z.string().nullable(),
  situacao: z.enum(SITUACOES_CNPJ),
  situacaoData: z.string().nullable(),
  abertura: z.string().nullable(),
  cnaePrincipal: z.object({ codigo: z.string(), descricao: z.string() }).nullable(),
  endereco: z.object({
    logradouro: z.string().nullable(),
    numero: z.string().nullable(),
    complemento: z.string().nullable(),
    bairro: z.string().nullable(),
    cidade: z.string().nullable(),
    uf: z.string().nullable(),
    cep: z.string().nullable(),
  }),
  /** Telefones da Receita (só dígitos, com DDD). Costumam ser do contador — nunca preencher WhatsApp sozinho. */
  telefones: z.array(z.string()),
  email: z.string().nullable(),
  fonte: z.string(),
  consultadoEm: z.string(),
});
export type DadosCnpj = z.infer<typeof dadosCnpjSchema>;

/** CNAE 7 dígitos → "4771-7/01". */
export function formatarCnae(codigo: string): string {
  const c = codigo.replace(/\D/g, '').padStart(7, '0');
  return `${c.slice(0, 4)}-${c.slice(4, 5)}/${c.slice(5)}`;
}
